import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  MapTrifold, ArrowClockwise, DownloadSimple, TrendUp, TrendDown, ArrowRight,
  CurrencyDollar, Truck, Info,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { US_STATE_GRID, US_STATE_NAMES } from "../data/usStateGrid";

const RANGES = [
  { label: "30d", value: 30 },
  { label: "90d", value: 90 },
  { label: "180d", value: 180 },
  { label: "1y", value: 365 },
  { label: "All", value: 3650 },
];

function fmtUSD(n) { return `$${Math.round(n || 0).toLocaleString()}`; }
function fmtInt(n) { return Math.round(n || 0).toLocaleString(); }

// Map a value 0..max → color intensity (0 = dim, 1 = full orange)
function stateColor(revenue, maxRevenue) {
  if (!revenue || !maxRevenue) return "rgba(255,255,255,0.03)";
  const intensity = Math.min(1, revenue / maxRevenue);
  // Interpolate from dark gray → FreightPilot orange
  const alpha = 0.15 + intensity * 0.85;
  return `rgba(255, 90, 31, ${alpha.toFixed(2)})`;
}

// Grid layout constants
const CELL = 44;
const GAP = 4;
const ROWS = 8;
const COLS = 12;

export default function LaneProfitabilityPage() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverState, setHoverState] = useState(null);
  const [minLoads, setMinLoads] = useState(1);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data: resp } = await client.get(`/reports/lane-profitability?days=${d}`);
      setData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load lane analysis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const { maxRevenue, filteredLanes } = useMemo(() => {
    if (!data) return { maxRevenue: 0, filteredLanes: [] };
    const revs = Object.values(data.state_activity || {}).map((s) => s.revenue || 0);
    const max = revs.length ? Math.max(...revs) : 0;
    const flanes = (data.lanes || []).filter((l) => l.load_count >= minLoads);
    return { maxRevenue: max, filteredLanes: flanes };
  }, [data, minLoads]);

  const downloadCSV = () => {
    if (!data?.lanes?.length) return;
    const header = ["Lane", "Loads", "Total miles", "Total revenue", "$/loaded mi", "Avg rate", "Avg miles/load"];
    const rows = filteredLanes.map((l) => [
      `${l.origin_state}→${l.destination_state}`,
      l.load_count, l.total_miles, l.total_revenue,
      l.per_loaded_mile, l.avg_rate, l.avg_miles,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lane-profitability-${data.range_start}-to-${data.range_end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hoverInfo = hoverState && data ? {
    abbr: hoverState,
    name: US_STATE_NAMES[hoverState] || hoverState,
    activity: data.state_activity?.[hoverState],
  } : null;

  return (
    <div className="space-y-6" data-testid="lane-profitability-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#FF5A1F] mb-1">LANE ANALYSIS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            State-lane profitability
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
            Which lanes actually pay? Aggregates delivered loads by origin state → destination
            state, showing revenue, $/loaded-mile, and load count per lane. Only counts loads
            where you've entered "City, ST" for both origin and destination.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 rounded-full border border-white/10 p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                disabled={loading}
                data-testid={`lanes-range-${r.value}`}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  days === r.value
                    ? "bg-white/10 text-white"
                    : "text-neutral-400 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load(days)}
            disabled={loading}
            data-testid="lanes-refresh-btn"
            className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 hover:border-white/25 disabled:opacity-50"
          >
            <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          {data?.lanes?.length > 0 && (
            <button
              onClick={downloadCSV}
              data-testid="lanes-export-csv"
              className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
            >
              <DownloadSimple size={12} weight="bold" /> CSV
            </button>
          )}
        </div>
      </div>

      {loading && !data ? (
        <div className="text-sm text-neutral-500 py-16 text-center">Loading…</div>
      ) : !data?.lanes?.length ? (
        <div className="rounded-lg border border-dashed border-white/10 py-12 text-center text-sm text-neutral-500">
          <MapTrifold size={20} className="mx-auto mb-2 text-neutral-600" />
          <div>No lane data yet.</div>
          <div className="mt-1 text-[11px]">
            Deliver some loads with "City, ST" origin + destination — auto-mileage will populate state fields.
          </div>
        </div>
      ) : (
        <>
          {/* Top-line stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lanes-stats">
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Unique lanes</div>
              <div className="font-display text-3xl font-semibold text-white tabular-nums">
                {data.unique_lanes}
              </div>
              <div className="text-[10px] text-neutral-600 mt-1">
                {data.total_loads} delivered load{data.total_loads !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-1">Best lane</div>
              {data.best_lane ? (
                <>
                  <div className="font-display text-2xl font-semibold text-emerald-400 tabular-nums">
                    {data.best_lane.origin_state}→{data.best_lane.destination_state}
                  </div>
                  <div className="text-[10px] text-neutral-600 mt-1 inline-flex items-center gap-1">
                    <TrendUp size={10} weight="bold" /> ${data.best_lane.per_loaded_mile}/mi · {data.best_lane.load_count} loads
                  </div>
                </>
              ) : <div className="text-neutral-500 text-sm">—</div>}
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-widest text-red-400 mb-1">Worst lane</div>
              {data.worst_lane && data.worst_lane.lane_key !== data.best_lane?.lane_key ? (
                <>
                  <div className="font-display text-2xl font-semibold text-red-400 tabular-nums">
                    {data.worst_lane.origin_state}→{data.worst_lane.destination_state}
                  </div>
                  <div className="text-[10px] text-neutral-600 mt-1 inline-flex items-center gap-1">
                    <TrendDown size={10} weight="bold" /> ${data.worst_lane.per_loaded_mile}/mi · {data.worst_lane.load_count} loads
                  </div>
                </>
              ) : <div className="text-neutral-500 text-sm">need more data</div>}
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-1">Avg $/mi</div>
              <div className="font-display text-3xl font-semibold text-[#FF5A1F] tabular-nums">
                ${data.avg_per_loaded_mile}
              </div>
              <div className="text-[10px] text-neutral-600 mt-1">
                across {fmtUSD(data.total_revenue)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            {/* Heatmap — US state tile grid */}
            <div className="xl:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="lanes-heatmap">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                  Revenue by state (touches)
                </div>
                <div className="text-[10px] text-neutral-600 inline-flex items-center gap-1">
                  <Info size={10} weight="fill" /> hover to inspect
                </div>
              </div>
              <div
                className="relative mx-auto"
                style={{
                  width: COLS * (CELL + GAP),
                  height: ROWS * (CELL + GAP),
                  maxWidth: "100%",
                }}
              >
                {Object.entries(US_STATE_GRID).map(([abbr, [row, col]]) => {
                  const st = data.state_activity?.[abbr];
                  const revenue = st?.revenue || 0;
                  const isHover = hoverState === abbr;
                  return (
                    <div
                      key={abbr}
                      data-testid={`state-tile-${abbr}`}
                      onMouseEnter={() => setHoverState(abbr)}
                      onMouseLeave={() => setHoverState(null)}
                      className={`absolute rounded transition-all cursor-pointer flex items-center justify-center text-[10px] font-mono font-bold ${
                        isHover ? "ring-2 ring-white z-10 scale-110" : ""
                      } ${revenue > 0 ? "text-white" : "text-neutral-600"}`}
                      style={{
                        left: col * (CELL + GAP),
                        top: row * (CELL + GAP),
                        width: CELL,
                        height: CELL,
                        backgroundColor: stateColor(revenue, maxRevenue),
                        border: revenue > 0 ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      {abbr}
                    </div>
                  );
                })}
              </div>
              {/* Legend */}
              <div className="mt-4 pt-4 border-t border-white/[0.05] flex items-center justify-between text-[10px] text-neutral-500">
                <span>$0</span>
                <div className="flex-1 mx-3 h-2 rounded-full"
                     style={{ background: "linear-gradient(to right, rgba(255,90,31,0.15), rgba(255,90,31,1))" }} />
                <span>{fmtUSD(maxRevenue)}</span>
              </div>
              {/* Hover panel */}
              {hoverInfo && (
                <div className="mt-4 rounded-lg bg-black/60 border border-white/10 p-3 text-xs"
                     data-testid="state-hover-panel">
                  <div className="text-white font-semibold">
                    {hoverInfo.abbr} · {hoverInfo.name}
                  </div>
                  {hoverInfo.activity ? (
                    <div className="mt-1 text-neutral-400 space-y-0.5">
                      <div>Revenue attributed: <span className="text-white tabular-nums">{fmtUSD(hoverInfo.activity.revenue)}</span></div>
                      <div>As origin: <span className="text-white">{hoverInfo.activity.origin_count}</span> · as destination: <span className="text-white">{hoverInfo.activity.destination_count}</span></div>
                    </div>
                  ) : (
                    <div className="mt-1 text-neutral-500">No delivered loads touching this state in this window.</div>
                  )}
                </div>
              )}
            </div>

            {/* Top lanes table */}
            <div className="xl:col-span-3 rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="lanes-table">
              <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                  All lanes · sorted by $/loaded mi
                </div>
                <div className="flex items-center gap-1 text-[10px] text-neutral-500">
                  <span>Min loads:</span>
                  {[1, 2, 3, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setMinLoads(n)}
                      data-testid={`lanes-minloads-${n}`}
                      className={`px-2 py-0.5 rounded-full transition-colors ${
                        minLoads === n
                          ? "bg-white/10 text-white"
                          : "text-neutral-500 hover:text-neutral-300"
                      }`}
                    >
                      {n}+
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto max-h-[540px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-neutral-500 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3">Lane</th>
                      <th className="text-right px-3 py-3">Loads</th>
                      <th className="text-right px-3 py-3">Total mi</th>
                      <th className="text-right px-3 py-3">Revenue</th>
                      <th className="text-right px-3 py-3">$/mi</th>
                      <th className="text-right px-3 py-3">Avg rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.05]">
                    {filteredLanes.map((lane, idx) => {
                      const isTop3 = idx < 3 && lane.per_loaded_mile > 0;
                      const isBottom = idx === filteredLanes.length - 1 && filteredLanes.length > 3;
                      return (
                        <tr key={lane.lane_key} className="hover:bg-white/[0.02]"
                            data-testid={`lane-row-${lane.origin_state}-${lane.destination_state}`}>
                          <td className="text-left px-4 py-3">
                            <div className="inline-flex items-center gap-1.5">
                              <span className="font-mono font-bold text-white">{lane.origin_state}</span>
                              <ArrowRight size={12} className="text-neutral-600" />
                              <span className="font-mono font-bold text-white">{lane.destination_state}</span>
                              {isTop3 && (
                                <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-semibold ml-2">
                                  TOP
                                </span>
                              )}
                              {isBottom && (
                                <span className="text-[9px] uppercase tracking-widest text-red-400 font-semibold ml-2">
                                  LOW
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-right px-3 py-3 tabular-nums text-neutral-300">
                            {lane.load_count}
                          </td>
                          <td className="text-right px-3 py-3 tabular-nums text-neutral-400">
                            {fmtInt(lane.total_miles)}
                          </td>
                          <td className="text-right px-3 py-3 tabular-nums text-emerald-400 font-medium">
                            {fmtUSD(lane.total_revenue)}
                          </td>
                          <td className="text-right px-3 py-3 tabular-nums text-[#FF5A1F] font-bold">
                            ${lane.per_loaded_mile}
                          </td>
                          <td className="text-right px-3 py-3 tabular-nums text-neutral-400">
                            {fmtUSD(lane.avg_rate)}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredLanes.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-sm text-neutral-500">
                          No lanes match minimum {minLoads} loads. Try lowering the threshold.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
